import ExportButton from 'components/common/ExportButton'
import { LOOKUP_TABLE_SINGLE } from 'constants/exportTypes'
import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import IconBack from '@material-ui/icons/ArrowBack'
import {
  Button,
  Checkbox,
  ListItem,
  Menu,
  TablePagination,
  TextField,
  Icon,
} from '@material-ui/core'
import { debounce, isEqual, cloneDeep } from 'lodash'
import cn from 'classnames'
import { Field, Form } from 'react-final-form'
import { Flex } from 'components/common/styles'
import {
  fetchLookupTablesTableInfo,
  fetchLookupTablesTableData,
  resetLookupTablesTableInfo,
  resetLookupTablesTableData,
  updateLookupTable,
  createLookupTableDataEntry,
  lookupTableAction,
  updateLookupTableRow,
  deleteLookupTablesTableData,
  setSingleTablePsfs,
  setIsLoading,
  resetSingleTablePsfs,
} from 'reducers/lookupTables/reducer'
import CircleLoader from 'components/Loading/CircleLoader'
import withPromise from 'helpers/withPromise'
import {
  ACTION_PUBLISH,
  ACTION_UNPUBLISH,
  STATUS_DRAFT,
} from 'constants/lookupTables'
import ConfirmDialog from 'components/common/Popup/ConfirmDialog'
import { ReactComponent as DragAndDropIcon } from 'assets/img/drag-and-drop-icon-small.svg'
import { ReactComponent as DeleteJobIcon } from 'assets/img/management-delete-job-icon.svg'
import { ReactComponent as SortIcon } from 'assets/img/sort-icon.svg'
import { ReactComponent as ArrowBottom } from 'assets/img/sort-arrow.svg'
import { ReactComponent as CheckIcon } from 'assets/img/check-icon.svg'
import Input from 'components/common/Form/Input'
import Psfs from 'lib/psfs/Psfs'
import { ENTER_KEY_CODE } from 'helpers/input'
import { StyledLookupSingleTable } from 'routes/App/Apps/LookupTables/LookupTables.styles'
import messages from './translate'

// eslint-disable-next-line max-statements
function SingleTable({
  intl: { formatMessage },
  isLoading,
  match: {
    params: { id: tableId },
  },
  history: { goBack },
  lookupTablesTableInfo,
  lookupTablesTableData,
  fetchLookupTablesTableInfo,
  fetchLookupTablesTableData,
  resetLookupTablesTableInfo,
  resetLookupTablesTableData,
  updateLookupTable,
  createLookupTableDataEntry,
  lookupTableAction,
  updateLookupTableRow,
  deleteLookupTablesTableData,
  setSingleTablePsfs,
  resetSingleTablePsfs,
  singleTablePsfs,
  setIsLoading,
  licensee: { latinAmericanNumberFormat },
}) {
  const { title: tableTitle, status } = lookupTablesTableInfo.toJS()
  const {
    columns: tableDataColumns,
    data: tableData,
    count,
  } = lookupTablesTableData.toJS()
  const [columns, setColumns] = useState([])
  const [newRowForm, setNewRowForm] = useState({})
  const [selectedRows, setSelectedRows] = useState([])
  const [tableForm, setTableForm] = useState([])
  const [newColumnFieldValue, setNewColumnFieldValue] = useState('')
  const [titleField, setTitleField] = useState('')
  const [searchFieldValue, setSearchFieldValue] = useState('')
  const [appliedSearchFieldValue, setAppliedSearchFieldValue] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(100)
  const [sortColumn, setSortColumn] = useState(null)
  const [newColumnAnchor, setNewColumnAnchor] = useState(null)
  const [tableActionMenuAnchor, setTableActionMenuAnchor] = useState(null)
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null)
  const [activeColumn, setActiveColumn] = useState({
    key: '',
    title: '',
  })
  const [addRowOpened, setAddRowOpened] = useState(false)
  const [isDialogOpened, setIsDialogOpened] = useState(false)
  const [confirmDialogSettings, setConfirmDialogSettings] = useState({
    popupAction: () => {},
    popupTitle: '',
    // eslint-disable-next-line react/jsx-no-useless-fragment
    popupMessage: <></>,
    confirmButtonText: 'delete',
    confirmButtonColor: 'scales-secondary',
  })
  const isDraft = status === STATUS_DRAFT
  const actionName = isDraft ? ACTION_PUBLISH : ACTION_UNPUBLISH
  const exportButtonParams = {
    type: LOOKUP_TABLE_SINGLE,
    psfs: singleTablePsfs,
    tableId,
  }

  const fetchSingleTableData = () => {
    withPromise(fetchLookupTablesTableInfo, { id: tableId }).then(response => {
      if (!response.error) {
        fetchLookupTablesTableData({
          id: tableId,
          psfs: singleTablePsfs,
        })
      }
    })
  }

  const setInitialNewRowForm = () => {
    const form = {}

    tableDataColumns.forEach(({ key }) => {
      form[key] = ''
    })

    setNewRowForm(form)
  }

  useEffect(
    () => () => {
      resetLookupTablesTableInfo()
      resetLookupTablesTableData()
      resetSingleTablePsfs()
    },
    [],
  )

  useEffect(() => {
    if (!isEqual(tableData, tableForm)) {
      setTableForm(cloneDeep(tableData))
      const form = {}

      tableDataColumns.forEach(({ key }) => {
        form[key] = ''
      })

      setNewRowForm({ ...form, ...newRowForm })
    }
  }, [tableData])

  useEffect(() => {
    setInitialNewRowForm()
    setColumns(tableDataColumns)
  }, [tableDataColumns])

  useEffect(() => {
    setTitleField(tableTitle)
  }, [tableTitle])

  useEffect(() => {
    fetchSingleTableData()
  }, [JSON.stringify(singleTablePsfs)])

  useEffect(() => {
    const { path } = singleTablePsfs
    const psfs = new Psfs(path)

    psfs.addSearch(appliedSearchFieldValue)

    setSingleTablePsfs(psfs)
  }, [appliedSearchFieldValue])

  useEffect(() => {
    const { path } = singleTablePsfs
    const psfs = new Psfs(path)

    psfs.addPagination(page + 1, rowsPerPage)

    setSingleTablePsfs(psfs)
  }, [page, rowsPerPage])

  useEffect(() => {
    if (sortColumn) {
      const { path } = singleTablePsfs
      const psfs = new Psfs(path)

      psfs.addSort(sortColumn.key, sortColumn.descSortOrder ? 'desc' : 'asc')

      setSingleTablePsfs(psfs)
    }
  }, [sortColumn])

  const updateTable = (data, callback) => {
    withPromise(updateLookupTable, {
      id: tableId,
      data,
      successMessage: formatMessage(messages.lookupTableActionSuccessMessage, {
        action: formatMessage(messages.update),
      }),
      errorMessage: formatMessage(messages.lookupTableActionErrorMessage, {
        action: formatMessage(messages.update),
      }),
    }).then(response => {
      if (!response.error) {
        setColumnMenuAnchor(null)
        setIsDialogOpened(null)
        fetchSingleTableData()
      }

      if (callback) {
        callback()
      }
    })
  }

  const deleteTableData = data => {
    withPromise(deleteLookupTablesTableData, {
      id: tableId,
      data,
      successMessage: formatMessage(messages.rowActionSuccessMessage, {
        action: formatMessage(messages.delete),
      }),
      errorMessage: formatMessage(messages.rowActionErrorMessage, {
        action: formatMessage(messages.delete),
      }),
    }).then(response => {
      if (!response.error) {
        setIsDialogOpened(null)
        setSelectedRows([])
        fetchSingleTableData()
      }
    })
  }

  const updateTableRow = (data, rowId) => {
    withPromise(updateLookupTableRow, {
      tableId,
      rowId,
      data,
      successMessage: formatMessage(messages.lookupTableActionSuccessMessage, {
        action: formatMessage(messages.update),
      }),
      errorMessage: formatMessage(messages.lookupTableActionErrorMessage, {
        action: formatMessage(messages.update),
      }),
    }).then(response => {
      if (!response.error) {
        fetchLookupTablesTableData({
          id: tableId,
          psfs: singleTablePsfs,
        })
      }
    })
  }

  const onCloseDeleteJobDialog = () => {
    setIsDialogOpened(false)
  }

  const onActionSuccess = successAction => {
    setIsDialogOpened(false)
    successAction()
  }

  const onConfirmTableAction = (
    tableId,
    actionName,
    successAction,
    successMessage,
    errorMessage,
  ) => {
    withPromise(lookupTableAction, {
      id: tableId,
      action: actionName,
      successMessage,
      errorMessage,
    }).then(response => {
      if (!response.error) {
        onActionSuccess(successAction)
      }
    })
  }

  const handleDragStart = e => {
    const { id } = e.target
    const idx = columns.indexOf(columns.find(({ key }) => key === id))
    e.dataTransfer.setData('colIdx', idx)
  }

  const handleDragOver = e => e.preventDefault()

  const handleOnDrop = e => {
    const { id } = e.currentTarget
    const droppedColIdx = columns.indexOf(columns.find(({ key }) => key === id))
    const draggedColIdx = e.dataTransfer.getData('colIdx')
    const tmpCols = [...columns]

    tmpCols[draggedColIdx] = columns[droppedColIdx]
    tmpCols[droppedColIdx] = columns[draggedColIdx]

    if (!isEqual(columns, tmpCols)) {
      const data = {
        title: tableTitle,
        columns: tmpCols,
      }

      setColumns(tmpCols)
      updateTable(data)
    }
  }

  const onOpenDialog = (
    popupTitle,
    popupMessage,
    popupAction,
    confirmButtonText,
    confirmButtonColor,
  ) => {
    setIsDialogOpened(true)
    setTableActionMenuAnchor(null)
    setConfirmDialogSettings({
      popupAction,
      popupTitle,
      popupMessage,
      confirmButtonText,
      confirmButtonColor,
    })
  }

  const addNewColumn = value => {
    const data = {
      title: tableTitle,
      columns: [
        ...columns,
        {
          title: value,
        },
      ],
    }

    updateTable(data, () => setNewColumnFieldValue(''))
  }

  const onOpenAddRow = () => {
    setAddRowOpened(true)
  }

  const onCloseAddRow = () => {
    setAddRowOpened(false)
  }

  const onAddRowSubmit = data => {
    withPromise(createLookupTableDataEntry, {
      data,
      id: tableId,
      successMessage: formatMessage(messages.rowActionSuccessMessage, {
        action: formatMessage(messages.add),
      }),
      errorMessage: formatMessage(messages.rowActionErrorMessage, {
        action: formatMessage(messages.add),
      }),
    }).then(response => {
      if (!response.error) {
        onCloseAddRow()
        setInitialNewRowForm()
        fetchLookupTablesTableData({
          id: tableId,
          psfs: singleTablePsfs,
        })
      }
    })
  }

  const renderCreateTableButton = () => (
    <Button
      disabled={!columns.length}
      className="lookup-tables-button primary"
      onClick={onOpenAddRow}>
      + {formatMessage(messages.newRow)}
    </Button>
  )

  const onChangeTableTitle = value => {
    const data = {
      title: value,
      columns: [...columns],
    }

    updateTable(data)
  }

  const updateTableData = (values, rowId) => {
    const data = {}

    values.forEach(({ key, value }) => {
      data[key] = value
    })

    updateTableRow(data, rowId)
  }

  const updateTableTitle = () => {
    const newColumns = columns.map(column => {
      if (column.key === activeColumn.key) {
        return activeColumn
      }

      return column
    })

    updateTable({
      title: tableTitle,
      columns: newColumns,
    })
  }

  const handleCheckBoxChange = rowId => {
    const idExists = selectedRows.find(id => id === rowId)

    if (idExists) {
      setSelectedRows([...selectedRows.filter(id => id !== rowId)])
    } else {
      setSelectedRows([...selectedRows, rowId])
    }
  }

  const handleAllSelect = () => {
    if (selectedRows.length !== tableForm.length) {
      setSelectedRows(tableForm.map(({ id }) => id))
    } else {
      setSelectedRows([])
    }
  }

  const onClearSearch = () => {
    setSearchFieldValue('')
    setAppliedSearchFieldValue('')
  }

  const handleSearchField = () => {
    if (searchFieldValue !== appliedSearchFieldValue) {
      setPage(0)
      setAppliedSearchFieldValue(searchFieldValue)
    }
  }

  const handleCloseAddRowButton = () => {
    onCloseAddRow()
    const form = {}

    tableDataColumns.forEach(({ key }) => {
      form[key] = ''
    })

    setNewRowForm({ ...newRowForm, ...form })
  }

  const addNewColumnDebounced = useCallback(debounce(addNewColumn, 1000), [
    columns,
    tableId,
  ])
  const updateTableDataDebounced = useCallback(
    debounce(updateTableData, 1000),
    [],
  )
  const handleSubmitDebounced = useCallback(debounce(onAddRowSubmit, 1000), [
    singleTablePsfs,
  ])

  const renderColumnMenu = () => (
    <Menu
      keepMounted
      anchorEl={columnMenuAnchor}
      open={!!columnMenuAnchor}
      className="management-ui-menu menu-title-input"
      onClose={() => {
        setColumnMenuAnchor(null)
        setActiveColumn({
          title: '',
          key: '',
        })
      }}>
      <TextField
        value={activeColumn.title}
        className="search-input wide menu-text-field"
        onChange={({ currentTarget: { value } }) => {
          setActiveColumn({
            ...activeColumn,
            title: value,
          })
        }}
        InputProps={{ disableUnderline: true }}
        onBlur={() => {
          updateTableTitle()
        }}
      />
      <ListItem
        className="menu-item context-menu-item-lookup"
        onClick={() => {
          const key = sortColumn && sortColumn.key
          const isCurrentColumnActive = activeColumn.key === key

          setSortColumn(isCurrentColumnActive ? null : activeColumn)
        }}>
        <ArrowBottom className="arrow-icon" />
        {formatMessage(messages.sort)}
        {activeColumn.key === (sortColumn && sortColumn.key) && (
          <CheckIcon className="check-icon" />
        )}
      </ListItem>
      <ListItem
        className="menu-item context-menu-item"
        onClick={() => {
          onOpenDialog(
            formatMessage(messages.delete),
            <>
              {formatMessage(messages.deleteTableDialogMessage)} &quot;
              <strong>{activeColumn.title}</strong>&quot;?
            </>,
            () => {
              const data = {
                title: tableTitle,
                columns: columns.filter(({ key }) => activeColumn.key !== key),
              }

              updateTable(data)
            },
            'delete',
            'scales-secondary',
          )
        }}>
        <DeleteJobIcon />
        {formatMessage(messages.deleteColumn)}
      </ListItem>
    </Menu>
  )

  const getRowValue = value => {
    if (latinAmericanNumberFormat && +value) {
      return String(value).replace('.', ',')
    }

    const numberValue = String(value).replace(',', '.')

    return +numberValue ? numberValue : value
  }

  const handleRowChange = ({ currentTarget: { value } }, rowIndex, key) => {
    const newTableForm = [...tableForm]

    const numberValue = String(value).replace(',', '.')

    newTableForm[rowIndex][key] = +numberValue ? numberValue : value

    setTableForm(newTableForm)
  }

  const renderTable = () => (
    <div className="lookup-tables-table-section">
      <Form
        initialValues={newRowForm}
        onSubmit={handleSubmitDebounced}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>
            <table>
              <thead>
                <tr>
                  <th>
                    <Checkbox
                      checked={
                        selectedRows.length === tableForm.length &&
                        selectedRows.length !== 0
                      }
                      href={null}
                      color="primary"
                      onChange={() => {
                        handleAllSelect()
                      }}
                    />
                  </th>
                  <th>ID</th>
                  {columns.map(({ title, key }) => (
                    <th
                      className="column-title"
                      id={key}
                      key={key}
                      draggable
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleOnDrop}
                      onContextMenu={event => {
                        event.preventDefault()
                        event.stopPropagation()

                        setColumnMenuAnchor(event.currentTarget)
                        setActiveColumn({
                          title,
                          key,
                        })
                      }}
                      onClick={() => {
                        const isSelected = sortColumn && sortColumn.key === key

                        setSortColumn({
                          key,
                          descSortOrder: isSelected
                            ? !sortColumn.descSortOrder
                            : false,
                        })
                      }}>
                      <DragAndDropIcon />
                      {title}
                      {!!sortColumn && sortColumn.key === key && (
                        <SortIcon
                          className={cn({
                            rotate: sortColumn.descSortOrder,
                            'sort-icon': true,
                          })}
                        />
                      )}
                    </th>
                  ))}
                  <th className="add-column-col">
                    <div
                      className="add-column-button"
                      onClick={({ currentTarget }) => {
                        setNewColumnAnchor(currentTarget)
                      }}
                    />
                    <Menu
                      keepMounted
                      anchorEl={newColumnAnchor}
                      open={!!newColumnAnchor}
                      className="management-ui-menu"
                      onClose={() => {
                        setNewColumnAnchor(null)
                      }}>
                      <TextField
                        value={newColumnFieldValue}
                        placeholder={formatMessage(messages.newColumn)}
                        className="search-input wide"
                        onChange={({ currentTarget: { value } }) => {
                          setNewColumnFieldValue(value)
                        }}
                        onKeyDown={({ keyCode }) => {
                          if (
                            keyCode === ENTER_KEY_CODE &&
                            newColumnFieldValue
                          ) {
                            setIsLoading(true)
                            addNewColumnDebounced(newColumnFieldValue)
                          }
                        }}
                        onBlur={({ currentTarget: { value } }) => {
                          if (value) {
                            addNewColumn(value)
                          }
                        }}
                      />
                    </Menu>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableForm.map((row, rowIndex) => {
                  const notRowData = ['dateCreated', 'dateUpdated', 'id']
                  const rowData = []
                  const sortedRowData = []
                  const isChecked = !!selectedRows.find(id => id === row.id)

                  Object.keys(row).forEach(key => {
                    if (notRowData.indexOf(key) < 0) {
                      rowData.push({
                        key,
                        value: row[key],
                      })
                    }
                  })

                  columns.forEach(({ key }) => {
                    sortedRowData.push(
                      rowData.find(({ key: rowKey }) => rowKey === key),
                    )
                  })

                  return (
                    <tr
                      key={row.id}
                      className={cn({ 'is-checked': isChecked })}>
                      <td>
                        <Checkbox
                          checked={isChecked}
                          color="primary"
                          onChange={() => {
                            handleCheckBoxChange(row.id)
                          }}
                        />
                      </td>
                      <td>{row.id}</td>
                      {sortedRowData.map(({ key, value }) => (
                        <td key={key}>
                          <TextField
                            placeholder="-"
                            value={getRowValue(value)}
                            className="table-edit-field"
                            onChange={event =>
                              handleRowChange(event, rowIndex, key)
                            }
                            style={{
                              width: `${
                                value === null ? 0 : value.length * 11
                              }px`,
                            }}
                            onKeyDown={({ keyCode }) => {
                              if (
                                keyCode === ENTER_KEY_CODE &&
                                !isEqual(tableData, tableForm)
                              ) {
                                setIsLoading(true)
                                updateTableDataDebounced(rowData, row.id)
                              }
                            }}
                            onBlur={() => {
                              if (!isEqual(tableData, tableForm)) {
                                updateTableData(rowData, row.id)
                              }
                            }}
                          />
                        </td>
                      ))}
                      <td className="add-column-col" />
                    </tr>
                  )
                })}
                {addRowOpened && (
                  <tr
                    className="add-row-form"
                    onKeyDown={({ keyCode }) => {
                      if (keyCode === ENTER_KEY_CODE) {
                        handleSubmit()
                      }
                    }}>
                    <td />
                    <td />
                    {Object.keys(newRowForm).map(key => (
                      <td key={key}>
                        <Field
                          placeholder="-"
                          name={key}
                          type="text"
                          component={Input}
                          disabled={submitting || isLoading}
                          parse={value => {
                            setNewRowForm({
                              ...newRowForm,
                              [key]: value,
                            })
                          }}
                        />
                      </td>
                    ))}
                    <td className="add-column-col add-row-actions">
                      <Button
                        className="lookup-tables-button primary"
                        onClick={handleSubmit}>
                        {formatMessage(messages.addRow)}
                      </Button>
                      <div
                        className="close-add-row-btn"
                        onClick={handleCloseAddRowButton}
                      />
                    </td>
                  </tr>
                )}
                {tableForm.length <= count && !addRowOpened && (
                  <tr className="add-row">
                    <td colSpan={tableDataColumns.length + 2}>
                      <Button
                        disabled={!columns.length}
                        className="add-row-button"
                        onClick={onOpenAddRow}>
                        + {formatMessage(messages.newRow)}
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </form>
        )}
      />
      <TablePagination
        count={count}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
        onChangePage={(event, nextPage) => {
          setPage(nextPage)
        }}
        onChangeRowsPerPage={(event, { props: { value } }) => {
          setPage(0)
          setRowsPerPage(value)
        }}
        component="div"
        className="table-pagination"
        labelRowsPerPage={formatMessage({ id: 'rows_per_page' })}
        backIconButtonProps={{
          'aria-label': formatMessage({ id: 'previous_page' }),
        }}
        nextIconButtonProps={{
          'aria-label': formatMessage({ id: 'next_page' }),
        }}
      />
    </div>
  )

  return (
    <StyledLookupSingleTable direction="column">
      <div className="lookup-tables-header">
        <h2 className="lookup-tables-title">
          <IconBack onClick={goBack} />
          {!!tableTitle && (
            <TextField
              value={titleField}
              className="table-edit-field"
              onChange={({ currentTarget: { value } }) => {
                setTitleField(value)
              }}
              style={{
                width: `${titleField.length * 12}px`,
              }}
              onBlur={({ currentTarget: { value } }) => {
                if (value && value !== tableTitle) {
                  onChangeTableTitle(value)
                }
              }}
            />
          )}
          {!!status && (
            <span
              className={`status-label ${status}`}
              onClick={({ currentTarget }) => {
                if (tableData.length) {
                  setTableActionMenuAnchor(currentTarget)
                }
              }}>
              {formatMessage(messages[status])}
            </span>
          )}
          <Menu
            anchorEl={tableActionMenuAnchor}
            open={!!tableActionMenuAnchor}
            className="management-ui-menu"
            onClose={() => {
              setTableActionMenuAnchor(null)
            }}>
            <ListItem
              className="menu-item context-menu-item secondary"
              onClick={() => {
                onOpenDialog(
                  formatMessage(messages[actionName]),
                  <>
                    {formatMessage(messages.lookupTableActionMessage, {
                      action: formatMessage(messages[actionName]),
                    })}{' '}
                    &quot;<strong>{tableTitle}</strong>&quot;?
                  </>,
                  () => {
                    onConfirmTableAction(
                      tableId,
                      actionName,
                      () => {
                        fetchLookupTablesTableInfo({ id: tableId })
                      },
                      formatMessage(messages.lookupTableActionSuccessMessage, {
                        action: formatMessage(messages[actionName]),
                      }),
                      formatMessage(messages.lookupTableActionErrorMessage, {
                        action: formatMessage(messages[actionName]),
                      }),
                    )
                  },
                  actionName,
                  isDraft ? 'scales-green' : 'scales-gray',
                )
              }}>
              {formatMessage(messages[actionName])}
            </ListItem>
          </Menu>
        </h2>
      </div>
      <div className="filter-bar-section">
        {selectedRows.length ? (
          <div className="delete-data-panel">
            <Button
              className="lookup-tables-button"
              onClick={() => {
                setSelectedRows([])
              }}>
              {formatMessage(messages.cancel)}
            </Button>
            <Button
              className="lookup-tables-button secondary"
              onClick={() => {
                onOpenDialog(
                  formatMessage(messages.delete),
                  <>
                    {formatMessage(messages.deleteTableDataDialogMessage)}?
                    {status !== STATUS_DRAFT && selectedRows.length === count && (
                      <div>
                        <strong>
                          {formatMessage(
                            messages.tableUnpublishConfirmationMessage,
                          )}
                        </strong>
                      </div>
                    )}
                  </>,
                  () => {
                    const data = [...selectedRows]

                    deleteTableData(data)
                  },
                  'delete',
                  'scales-secondary',
                )
              }}>
              {formatMessage(messages.delete)} {selectedRows.length}
            </Button>
          </div>
        ) : (
          <div className="filter-block">
            <div className="search-block">
              <Icon className="search-block-icon search">search</Icon>
              <TextField
                value={searchFieldValue}
                placeholder={formatMessage(messages.search)}
                className="search-input wide"
                onChange={({ currentTarget: { value } }) => {
                  setSearchFieldValue(value)
                }}
                onBlur={() => {
                  handleSearchField()
                }}
                onKeyDown={e => {
                  if (e.keyCode === ENTER_KEY_CODE) {
                    handleSearchField()
                  }
                }}
              />
              {!!searchFieldValue && (
                <Icon
                  className="search-block-icon clear"
                  onClick={onClearSearch}>
                  clear
                </Icon>
              )}
            </div>
          </div>
        )}
        <Flex>
          <ExportButton
            className="lookup-export-button"
            params={exportButtonParams}
            isNewDesign
          />
          {!addRowOpened && renderCreateTableButton()}
        </Flex>
      </div>
      {renderTable()}
      {renderColumnMenu()}
      <ConfirmDialog
        open={isDialogOpened}
        cancel={{
          color: 'default',
          variant: 'outlined',
          customText: formatMessage(messages.cancel),
        }}
        confirm={{
          color: confirmDialogSettings.confirmButtonColor,
          customText: formatMessage(
            messages[confirmDialogSettings.confirmButtonText],
          ),
        }}
        onClose={onCloseDeleteJobDialog}
        onConfirmAction={confirmDialogSettings.popupAction}
        className="dialog-primary-custom"
        title={confirmDialogSettings.popupTitle}
        message={confirmDialogSettings.popupMessage}
      />
      <CircleLoader loading={isLoading} />
    </StyledLookupSingleTable>
  )
}

export default connect(
  state => ({
    isLoading: state.getIn(['lookupTables', 'isLoading']),
    lookupTablesTableInfo: state.getIn([
      'lookupTables',
      'lookupTablesTableInfo',
    ]),
    lookupTablesTableData: state.getIn([
      'lookupTables',
      'lookupTablesTableData',
    ]),
    singleTablePsfs: state.getIn(['lookupTables', 'singleTablePsfs']),
    licensee: state.getIn(['profile', 'profile', 'licensee']),
  }),
  {
    fetchLookupTablesTableInfo,
    fetchLookupTablesTableData,
    resetLookupTablesTableInfo,
    resetLookupTablesTableData,
    updateLookupTable,
    createLookupTableDataEntry,
    lookupTableAction,
    updateLookupTableRow,
    deleteLookupTablesTableData,
    setSingleTablePsfs,
    resetSingleTablePsfs,
    setIsLoading,
  },
)(injectIntl(SingleTable))
